import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldShowFloorplanV2 } from '@components/floorplan-v2/should-show-floorplan-v2';
import { ORGANIZER_INVITATION_ROUTE, SELECT_VENUE_ROUTE, USER_SETTINGS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContextValue, CompanyClubState, VenueAccess } from '@contexts/company-club/company-club.type';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { FeatureFlagContext } from '@contexts/feature-flag/feature-flag-context';
import { UserContext } from '@contexts/user/user-context';
import { Club } from '@domain/club';
import { Company } from '@domain/company';
import { useGetCompanyClub } from '@hooks/company-club/use-get-company-club';
import { usePostHog } from '@posthog/react';
import { clearCompanyContext, identifyCompanyGroupProperties } from '@posthog-app/company-identification';
import { useFeatureFlag, useFeatureFlagPayload, useFeatureFlagState } from '@posthog-app/use-feature-flag';
import { getVenueFromPath } from '@utils/get-venue-from-path/get-venue-from-path';
import { toResaleSettingsVisibility } from '@utils/to-resale-settings-visibility/to-resale-settings-visibility';

import { companyClubEmptyState } from './constants';

export const CompanyClubProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setCompanyAuthId, setCompanyClubId } = useContext(CompanyAuthContext);
  const { testCompanyIds, externalPlatformEnabledIds } = useContext(FeatureFlagContext);
  const { isAuthenticated } = useContext(AuthContext);
  const { userState } = useContext(UserContext);
  const posthog = usePostHog();
  const { companyId, clubSlug } = getVenueFromPath(pathname);

  const isTestVenueCompany = (testCompanyIds as string).split(',').includes(companyId);
  const isExternalPlatformEnabledForCompany = (externalPlatformEnabledIds as string).split(',').includes(companyId);
  // The monolith reads the company id as an int and answers anything else with a 500: a URL like
  // `/undefined/<slug>` or a pasted `/barcelona/<slug>` goes to the venue selection instead.
  const isCompanyClubContextEnabled = /^\d+$/.test(companyId) && !!clubSlug && !clubSlug.includes(':');

  const [companyClubState, setCompanyClubState] = useState<CompanyClubState>({
    id: '',
    legacyId: '',
    isSiaeImportLimited: false,
  });

  const [companyState, setCompanyState] = useState<Company>({
    id: '',
    legacyId: '',
    name: '',
    commercialName: '',
    defaultChannel: {
      id: '',
      name: '',
      slug: '',
    },
  });

  const [clubState, setClubState] = useState<Club>({
    id: '',
    legacyId: '',
    coverUrl: '',
    logoUrl: '',
    name: '',
    slug: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
    city: {
      country: {
        id: '',
        name: '',
        isoCode: '',
        currency: '',
      },
      id: '',
      legacyId: '',
      coverUrl: '',
      name: '',
      slug: '',
      timezone: '',
    },
  });

  const isFloorplanV2Active = shouldShowFloorplanV2(
    useFeatureFlag('floorplan-interactive-editor'),
    useFeatureFlagPayload('floorplan-interactive-editor'),
    companyState.legacyId
  );

  const isExtraGuestsForBottleServiceActive = useFeatureFlag('extra-guests-for-bottle-services');

  // Also covers the pass and admission-preset galleries: they share the admission drawer chassis.
  const isAdmissionImageGalleryActive = useFeatureFlag('admission-image-gallery');
  const isAddonImageGalleryActive = useFeatureFlag('addon-image-gallery');
  const isAdmissionIncludeDrinksActive = useFeatureFlag('admission-include-drinks');
  const isAddonTermsAndConditionsLinkActive = useFeatureFlag('addon-terms-and-conditions-link');

  // Also gates the SIAE booking-plugin share link, not only the widget sign-in section.
  const isWidgetSignInEnabled = useFeatureFlag('widget-config-enable-sign-in');

  // OFF must stay inert, not just hidden: the backend half ships separately, so no resale key may
  // reach the form defaults or the save payload.
  const isWidgetResaleActive = useFeatureFlag('widget-config-resale-section');
  // OFF restores the old hardcoded snippet and fires no event-list request, so the UI never
  // advertises a `data-mode` the widget cannot parse yet.
  const isWidgetEmbedGeneratorActive = useFeatureFlag('widget-config-embed-code-generator');

  const isSecretLocationActive = useFeatureFlag('event-secret-location');

  // OFF must stay inert, not just hidden: the block ships ahead of the B2C organizer page, so its
  // option queries are `enabled`-gated and no form state is registered.
  const isOrganizerPublicProfileActive = useFeatureFlag('organizer-public-profile');

  // One flag for both the company default and the per-event section: a default the organizer cannot
  // override per event would be worse than either state.
  const isDelayedDeliveryActive = useFeatureFlag('delayed-delivery-settings');

  // One flag for the company default and the per-event section, like delayed delivery. OFF must stay
  // inert: the endpoints ship separately, so no resale query fires and nothing reaches a save payload.
  const isResaleSettingsActive = useFeatureFlag('resale-settings');
  // The payload hides blocks of the form whose backend half is not live yet. posthog-js parses it again on
  // every flags reload, so the memo keys on the three booleans rather than on the object.
  const { activationMode, organizerFee, timeSettings } = toResaleSettingsVisibility(
    useFeatureFlagPayload('resale-settings')
  );
  const resaleSettingsVisibility = useMemo(
    () => ({ activationMode, organizerFee, timeSettings }),
    [activationMode, organizerFee, timeSettings]
  );

  // One flag for the company default and the per-event section, like resale. Separate from
  // `event-report-ticket-alerts`, which is already on where these endpoints are not live yet.
  const isTicketAlertsSettingsActive = useFeatureFlag('ticket-alerts-settings');

  // Separate from `resale-settings`: the report reads B2C marketplace sales, so a company can need
  // it without the settings.
  const isEventReportResaleActive = useFeatureFlag('event-report-resale');

  // Gates the whole of Sales Pace (velocity gauge and chart section); the roles that may see it are
  // resolved on top of this by `useIsSalesPaceEnabled`.
  const isEventReportSalesPaceActive = useFeatureFlag('event-report-sales-pace');

  // Gates the Ticket alerts card; who may ask for its stats is resolved on top of this by
  // `useIsTicketAlertsEnabled`. Callers must gate the export with its own permission.
  const isEventReportTicketAlertsActive = useFeatureFlag('event-report-ticket-alerts');

  // Read in three states: the invite page must not redirect away (losing its token) while PostHog
  // is still loading, which on a first visit can outlast `/me`. Until `/flags` answers, PostHog
  // returns the value stored from the last session, so a stored `false` must wait for a fresh answer;
  // a stored `true` can let the page through. A flag missing from that answer stays `undefined`, so
  // ask PostHog whether it answered.
  const addOrganizerToVenueFlag = useFeatureFlagState('add-organizer-to-venue');
  const [haveFlagsLoaded, setHaveFlagsLoaded] = useState(false);
  useEffect(() => posthog?.onFeatureFlags(() => setHaveFlagsLoaded(true)), [posthog]);
  const isAddOrganizerToVenueActive = addOrganizerToVenueFlag ?? false;
  const isAddOrganizerToVenueLoading = addOrganizerToVenueFlag !== true && !haveFlagsLoaded;

  const isVideoCoverUploadActive = useFeatureFlag('video-cover-upload');

  const setEmptyCompanyClubState = useCallback(() => {
    setClubState(companyClubEmptyState.clubState);
    setCompanyClubState(companyClubEmptyState.companyClubState);
    setCompanyState(companyClubEmptyState.companyState);
  }, []);

  const {
    data: companyClub,
    error: companyClubError,
    isLoading: isLoadingCompanyClub,
  } = useGetCompanyClub({
    companyId,
    clubSlug,
    options: {
      enabled: isCompanyClubContextEnabled && !!userState,
    },
  });

  // The synced states above lag a render behind the response and keep the previous venue's values
  // while a new one loads; the query data is keyed by venue, so it never does either.
  const venueAccess = useMemo<VenueAccess | null>(
    () => (companyClub ? { isRevoked: companyClub.isAccessRevoked, venueName: companyClub.club.name } : null),
    [companyClub]
  );

  const shouldNavigateToSelectVenue =
    userState &&
    !isCompanyClubContextEnabled &&
    pathname !== SELECT_VENUE_ROUTE &&
    pathname !== USER_SETTINGS_ROUTE &&
    pathname !== ORGANIZER_INVITATION_ROUTE;

  useEffect(() => {
    if (shouldNavigateToSelectVenue) {
      navigate(SELECT_VENUE_ROUTE, { replace: true });
    }
  }, [shouldNavigateToSelectVenue, navigate]);

  useEffect(() => {
    if (!!companyClubError && companyClubError.status === 403 && isAuthenticated) {
      navigate(SELECT_VENUE_ROUTE, { replace: true });
    }
  }, [companyClubError, navigate, isAuthenticated]);

  useEffect(() => {
    if (companyClub && isCompanyClubContextEnabled && !companyClubError?.status) {
      const { id, legacyId, isSiaeImportLimited } = companyClub;
      setCompanyClubState({
        id,
        legacyId,
        isSiaeImportLimited,
      });
      if (companyClub?.club) setClubState(companyClub.club);
      if (companyClub?.company) {
        setCompanyState(companyClub.company);
        const legacyCompanyId = Number.parseInt(companyClub.company.legacyId);
        // Impersonated sessions included on purpose: the company group is what
        // company-scoped feature flags resolve against, and capture is off for the whole
        // session (syncImpersonationCapture), so $groupidentify is never sent.
        if (posthog && !userState?.isSuperAdmin && !Number.isNaN(legacyCompanyId)) {
          identifyCompanyGroupProperties(posthog, {
            companyId: legacyCompanyId,
            name: companyClub.company.name,
            commercialName: companyClub.company.commercialName,
          });
        }
      }
      setCompanyAuthId(companyClub.company.id);
      setCompanyClubId(companyClub.club.id);
    } else if (!isCompanyClubContextEnabled) {
      setEmptyCompanyClubState();
      if (posthog) clearCompanyContext(posthog);
    }
  }, [
    companyId,
    clubSlug,
    companyClub,
    isCompanyClubContextEnabled,
    setEmptyCompanyClubState,
    userState?.isSuperAdmin,
    companyClubError,
    setCompanyAuthId,
    setCompanyClubId,
    posthog,
  ]);

  const memoizedContextValue = useMemo<CompanyClubContextValue>(
    () => ({
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isWidgetResaleActive,
      isWidgetEmbedGeneratorActive,
      isSecretLocationActive,
      isOrganizerPublicProfileActive,
      isDelayedDeliveryActive,
      isResaleSettingsActive,
      isTicketAlertsSettingsActive,
      resaleSettingsVisibility,
      isEventReportResaleActive,
      isEventReportSalesPaceActive,
      isEventReportTicketAlertsActive,
      isAddOrganizerToVenueActive,
      isAddOrganizerToVenueLoading,
      isVideoCoverUploadActive,
      isLoadingCompanyClub,
      venueAccess,
    }),
    [
      companyClubState,
      companyState,
      clubState,
      setCompanyClubState,
      setCompanyState,
      setClubState,
      setEmptyCompanyClubState,
      isTestVenueCompany,
      isExternalPlatformEnabledForCompany,
      isFloorplanV2Active,
      isExtraGuestsForBottleServiceActive,
      isAdmissionImageGalleryActive,
      isAddonImageGalleryActive,
      isAdmissionIncludeDrinksActive,
      isAddonTermsAndConditionsLinkActive,
      isWidgetSignInEnabled,
      isWidgetResaleActive,
      isWidgetEmbedGeneratorActive,
      isSecretLocationActive,
      isOrganizerPublicProfileActive,
      isDelayedDeliveryActive,
      isResaleSettingsActive,
      isTicketAlertsSettingsActive,
      resaleSettingsVisibility,
      isEventReportResaleActive,
      isEventReportSalesPaceActive,
      isEventReportTicketAlertsActive,
      isAddOrganizerToVenueActive,
      isAddOrganizerToVenueLoading,
      isVideoCoverUploadActive,
      isLoadingCompanyClub,
      venueAccess,
    ]
  );

  return <CompanyClubContext.Provider value={memoizedContextValue}>{children}</CompanyClubContext.Provider>;
};
